/**
 * Executing a confirmed plan.
 *
 * Framework-free on purpose: no React, no axios, no FormData at module scope.
 * The API object is INJECTED (`api`), which is what lets the tests drive a full
 * build against a recorder and assert the exact call sequence. It also keeps
 * axios out of this module's graph — `src/App.test.js` already fails to load at
 * HEAD because axios ships ESM that jest does not transform, and any suite that
 * imports it inherits that failure.
 *
 * The step order — **cluster, then project, then model, then upload** — is not
 * arbitrary. Clustering is the step most likely to time out (a CPU ResNet18
 * forward over a few hundred images, behind a 60s proxy read timeout), and it
 * is the only step that writes nothing. Running it first means a failure there
 * leaves the account exactly as it was. Reversed, the user is left with an
 * empty project and a model whose name is now permanently taken — and neither
 * create endpoint is idempotent, so pressing the button again makes a second one.
 */
import { MAX_CLUSTER_SAMPLE, evenSample } from './datasetScan';
import {
  UNSTRUCTURED_CLASS_ID,
  buildClassificationPayload,
  buildDetectionPayload,
  toFormData,
} from './datasetPayload';

export const STEPS = Object.freeze(['cluster', 'project', 'model', 'upload', 'verify']);

/** How long to wait for the background upload to land before calling it partial. */
const VERIFY_TIMEOUT_MS = 30000;
const VERIFY_INTERVAL_MS = 2000;
/**
 * A hard stop on the poll independent of the clock.
 *
 * The deadline alone is enough in a browser, but it makes the loop's
 * termination depend on a clock advancing — and an injected or frozen clock
 * then spins forever. A bound that cannot be argued with is cheaper than
 * trusting the caller.
 */
const VERIFY_MAX_POLLS = Math.ceil(VERIFY_TIMEOUT_MS / VERIFY_INTERVAL_MS) + 2;

/** How many times to try a suffixed name when one is already taken. */
const MAX_NAME_ATTEMPTS = 5;

const SUCCESS = 'success';

class CancelledError extends Error {
  constructor() {
    super('cancelled');
    this.name = 'CancelledError';
    this.cancelled = true;
  }
}

const throwIfCancelled = (signal) => {
  if (signal && signal.aborted) throw new CancelledError();
};

const isCancellation = (error) =>
  !!error &&
  (error.cancelled ||
    error.name === 'AbortError' ||
    error.name === 'CanceledError' ||
    error.code === 'ERR_CANCELED' ||
    error.message === 'canceled');

const wait = (ms, signal) =>
  new Promise((resolve, reject) => {
    const timer = setTimeout(resolve, ms);
    if (signal) {
      signal.addEventListener(
        'abort',
        () => {
          clearTimeout(timer);
          reject(new CancelledError());
        },
        { once: true },
      );
    }
  });

/** `Pets` -> `Pets-2` -> `Pets-3`. */
const suffixed = (name, attempt) => (attempt <= 1 ? name : `${name}-${attempt}`);

/**
 * Resolve the project to write into.
 *
 * An exact name match that already exists is REUSED rather than duplicated —
 * "make a model inside a project called X" means that project when it exists.
 * Only when creation fails for a name nothing visible owns (a soft-deleted
 * project still holds the name: the duplicate check does not filter
 * `isDeleted`, but `get_models` does) does this fall back to suffixing.
 */
const resolveProject = async ({ plan, ownerId, api, signal, note }) => {
  if (plan.target.projectId) {
    return { id: plan.target.projectId, name: plan.target.projectName, reused: true };
  }

  const wanted = plan.target.projectName;
  let existing = [];
  try {
    existing = await api.listProjects(ownerId, signal);
  } catch (error) {
    if (isCancellation(error)) throw error;
    // A failed lookup only costs us the reuse optimisation.
    existing = [];
  }
  const match = existing.find(
    (project) => (project.name || '').toLowerCase() === wanted.toLowerCase(),
  );
  if (match) {
    note('projectReused', { name: match.name });
    return { id: match.id, name: match.name, reused: true };
  }

  for (let attempt = 1; attempt <= MAX_NAME_ATTEMPTS; attempt += 1) {
    throwIfCancelled(signal);
    const name = suffixed(wanted, attempt);
    // A duplicate name comes back as HTTP 200 with data: null — branching on
    // the status here would hand `null` to the next step and 500 three calls later.
    const result = await api.createProject({ name, detail: plan.summary || '', ownerId }, signal);
    if (result.message === SUCCESS && result.data) {
      if (attempt > 1) note('projectRenamed', { name });
      return { id: String(result.data), name, reused: false };
    }
  }
  throw new Error(`Could not create a project named "${wanted}" — the name is already taken.`);
};

/** Same contract as the project, except the new id lives at `data._id`. */
const resolveModel = async ({ plan, projectId, ownerId, api, signal, note }) => {
  const wanted = plan.target.modelName;
  for (let attempt = 1; attempt <= MAX_NAME_ATTEMPTS; attempt += 1) {
    throwIfCancelled(signal);
    const name = suffixed(wanted, attempt);
    const result = await api.createModel(
      {
        name,
        detail: plan.summary || '',
        ownerId,
        projectId,
        // Whatever the archive decided. 'bounding_box' is the backend's string.
        modelType: plan.target.modelType || 'classification',
      },
      signal,
    );
    if (result.message === SUCCESS && result.data && result.data._id) {
      if (attempt > 1) note('modelRenamed', { name });
      return { id: String(result.data._id), name, doc: result.data };
    }
  }
  throw new Error(`Could not create a model named "${wanted}" — the name is already taken.`);
};

/**
 * Decide which class each image belongs to.
 *
 * Returns `[{path, name, classId}]`, where classId 0 means "import unlabeled".
 */
export const assignClasses = async ({ plan, items, readBlob, downscale, api, signal, onProgress, note }) => {
  const mode = plan.grouping.mode;

  if (mode === 'annotations') {
    // The archive already said which class every box belongs to. There is
    // nothing to decide and nothing to upload to decide it.
    return items;
  }

  if (mode === 'unlabeled') {
    return items.map((item) => ({ ...item, classId: UNSTRUCTURED_CLASS_ID }));
  }

  if (mode === 'folders') {
    const bySource = new Map();
    plan.classes.forEach((entry) => bySource.set(entry.source, entry.id));
    return items.map((item) => ({
      ...item,
      classId: bySource.has(item.folder) ? bySource.get(item.folder) : UNSTRUCTURED_CLASS_ID,
    }));
  }

  // cluster
  const sample = evenSample(items, MAX_CLUSTER_SAMPLE);
  if (sample.length < items.length) {
    note('clusterSampled', { sampled: sample.length, total: items.length });
  }

  const files = [];
  for (let index = 0; index < sample.length; index += 1) {
    throwIfCancelled(signal);
    const item = sample[index];
    const raw = await readBlob(item.path);
    // Downscaled before it goes on the wire. The server resizes to 256 and
    // centre-crops to 224 anyway, so grouping quality is unchanged while the
    // payload drops by roughly fifty times — which is what keeps a few hundred
    // images inside the proxy's read timeout.
    const blob = downscale ? await downscale(raw) : raw;
    files.push({ name: item.name, blob });
    if (onProgress) onProgress(Math.round(((index + 1) / sample.length) * 50));
  }

  const result = await api.clusterImages(
    {
      files,
      numClusters: plan.grouping.numClasses || null,
      maxClusters: plan.grouping.maxClasses || null,
    },
    { signal, onProgress: (percent) => onProgress && onProgress(50 + Math.round(percent / 2)) },
  );

  // cluster_id is 0-based; class id 0 is the backend's unlabeled bucket, so
  // every cluster shifts up by one.
  const classIdByPath = new Map();
  result.assignments.forEach((assignment) => {
    const item = sample[assignment.index];
    if (!item) return;
    classIdByPath.set(item.path, Number(assignment.cluster_id) + 1);
  });

  return items.map((item) => ({
    ...item,
    classId: classIdByPath.has(item.path) ? classIdByPath.get(item.path) : UNSTRUCTURED_CLASS_ID,
  }));
};

/**
 * Reconcile the plan's classes with what the clustering actually produced.
 *
 * `/cluster/auto` picks its own k when none was given, so the plan's placeholder
 * names may be the wrong length.
 */
export const reconcileClasses = (plan, assigned) => {
  if (plan.grouping.mode !== 'cluster') return plan.classes;
  const used = [...new Set(assigned.map((item) => item.classId))]
    .filter((id) => id !== UNSTRUCTURED_CLASS_ID)
    .sort((a, b) => a - b);
  return used.map((id, index) => {
    const existing = plan.classes[index];
    return {
      id,
      name: existing ? existing.name : `class ${id}`,
      count: assigned.filter((item) => item.classId === id).length,
      source: null,
    };
  });
};

/**
 * Confirm the images actually landed.
 *
 * `save_classification_data` returns as soon as it has read the bytes into
 * memory; the MinIO puts and the Mongo inserts run in a FastAPI BackgroundTask
 * afterwards, with no per-file error handling — one image PIL cannot process
 * takes the whole batch down, and the response said "success" long before. So
 * the version is read back and the rows are counted.
 */
const verifyUpload = async ({
  versionId, expected, api, signal, now, sleep, isDetection, onProgress,
}) => {
  const clock = now || (() => Date.now());
  const pause = sleep || wait;
  const deadline = clock() + VERIFY_TIMEOUT_MS;
  let counted = 0;
  let polls = 0;

  while (clock() < deadline && polls < VERIFY_MAX_POLLS) {
    polls += 1;
    throwIfCancelled(signal);
    try {
      const body = isDetection
        ? await api.getDetectionVersion(versionId, signal)
        : await api.getClassificationVersion(versionId, signal);
      counted = Array.isArray(body.model_data_items_info) ? body.model_data_items_info.length : 0;
      // The rows land in a BackgroundTask, so this genuinely climbs. Reporting
      // it is the difference between a 30s wait that is visibly working and one
      // that reads as frozen at the very last step of the import.
      if (onProgress) {
        onProgress(expected > 0 ? Math.round((Math.min(counted, expected) / expected) * 100) : 0, counted);
      }
      if (counted >= expected) return { imported: counted, complete: true };
    } catch (error) {
      if (isCancellation(error)) throw error;
      // A read that fails is not evidence the upload failed; keep waiting.
    }
    await pause(VERIFY_INTERVAL_MS, signal);
  }
  return { imported: counted, complete: counted >= expected };
};

/**
 * Run a confirmed plan.
 *
 * `onStep(id, state, detail)` is called with state `running` | `done` |
 * `skipped` | `failed`, so the card can render progress without this module
 * knowing anything about React.
 */
export const runPlan = async ({
  plan,
  /**
   * The interview's own clustering, as a Map of image path -> class id.
   *
   * Not an optimisation. `/cluster/auto` picks its own k when none was given,
   * so a second run can legitimately return a DIFFERENT number of groups — and
   * then the classes that get created are not the ones the user reviewed and
   * approved. Re-running it would also re-read and re-upload the whole
   * downscaled sample, which is the slowest step in the import.
   */
  preassigned,
  items,
  readBlob,
  downscale,
  ownerId,
  api,
  signal,
  onStep,
  makeId,
  now,
  sleep,
}) => {
  const notes = [];
  const note = (codeKey, params) => notes.push({ codeKey, params: params || {} });
  const step = (id, state, detail) => onStep && onStep(id, state, detail || {});

  const result = {
    status: 'failed',
    projectId: null,
    projectName: null,
    modelId: null,
    modelName: null,
    /**
     * The whole model document as the API returned it.
     *
     * The labeling screens read `location.state.model`, so a card that wants to
     * offer "Start labeling" has to hand them this. It is also the reason the
     * prompt bans markdown links to those routes: a plain link arrives with no
     * state and throws. A card can carry state; a link cannot.
     */
    modelDoc: null,
    versionId: null,
    imported: 0,
    expected: 0,
    classes: [],
    notes,
    error: null,
  };

  const isDetection = !!plan && plan.target && plan.target.modelType === 'bounding_box';

  try {
    if (!plan) throw new Error('There is no plan to run.');
    if (!ownerId) throw new Error('You must be signed in to import a dataset.');
    if (!items || !items.length) throw new Error('There are no images to import.');

    // ---- 1. group ---------------------------------------------------------
    let assigned;
    if (plan.grouping.mode === 'cluster' && preassigned && preassigned.size) {
      // Already grouped, during the interview, with the user watching — and
      // already shown to them on the review card. Reuse it verbatim.
      step('cluster', 'running', { percent: 100 });
      assigned = items.map((item) => ({
        ...item,
        classId: preassigned.has(item.path)
          ? preassigned.get(item.path)
          : UNSTRUCTURED_CLASS_ID,
      }));
      step('cluster', 'done', { percent: 100 });
    } else if (plan.grouping.mode === 'cluster') {
      step('cluster', 'running', { percent: 0 });
      assigned = await assignClasses({
        plan, items, readBlob, downscale, api, signal, note,
        onProgress: (percent) => step('cluster', 'running', { percent }),
      });
      step('cluster', 'done', { percent: 100 });
    } else {
      step('cluster', 'skipped');
      assigned = await assignClasses({ plan, items, readBlob, downscale, api, signal, note });
    }
    throwIfCancelled(signal);

    const classes = reconcileClasses(plan, assigned);
    result.classes = classes;

    // ---- 2. project -------------------------------------------------------
    step('project', 'running');
    const project = await resolveProject({ plan, ownerId, api, signal, note });
    result.projectId = project.id;
    result.projectName = project.name;
    step('project', 'done', { name: project.name, reused: project.reused });
    throwIfCancelled(signal);

    // ---- 3. model ---------------------------------------------------------
    step('model', 'running');
    const model = await resolveModel({ plan, projectId: project.id, ownerId, api, signal, note });
    result.modelId = model.id;
    result.modelName = model.name;
    result.modelDoc = model.doc || null;
    step('model', 'done', { name: model.name });
    throwIfCancelled(signal);

    // ---- 4. upload --------------------------------------------------------
    step('upload', 'running', { percent: 0 });
    const build = isDetection ? buildDetectionPayload : buildClassificationPayload;
    const payload = build({ ...plan, classes }, assigned, {
      projectId: project.id,
      modelId: model.id,
      ownerId,
      makeId,
    });
    result.expected = payload.counts.total;

    const blobs = new Map();
    for (let index = 0; index < payload.parts.length; index += 1) {
      throwIfCancelled(signal);
      const part = payload.parts[index];
      // eslint-disable-next-line no-await-in-loop
      blobs.set(part.path, await readBlob(part.path));
      step('upload', 'running', {
        percent: Math.round(((index + 1) / payload.parts.length) * 30),
      });
    }

    const form = toFormData(payload, blobs);
    const send = isDetection ? api.saveDetectionData : api.saveClassificationData;
    const saved = await send(form, {
      signal,
      onProgress: (percent) => step('upload', 'running', { percent: 30 + Math.round(percent * 0.7) }),
    });
    // Free the bytes as soon as they are on the wire.
    blobs.clear();

    const versionId = saved.model_info_id;
    if (!versionId) throw new Error(saved.message || 'The upload did not return a version.');
    result.versionId = versionId;
    step('upload', 'done', { percent: 100 });
    throwIfCancelled(signal);

    // ---- 5. verify --------------------------------------------------------
    step('verify', 'running');
    const verified = await verifyUpload({
      versionId, expected: result.expected, api, signal, now, sleep, isDetection,
      onProgress: (percent, counted) => step('verify', 'running', { percent, counted }),
    });
    result.imported = verified.imported;
    result.status = verified.complete ? 'done' : 'partial';
    step('verify', verified.complete ? 'done' : 'failed', {
      imported: verified.imported,
      expected: result.expected,
    });
    return result;
  } catch (error) {
    if (isCancellation(error)) {
      result.status = 'cancelled';
      result.error = null;
    } else {
      result.status = 'failed';
      result.error = error && error.message ? error.message : String(error);
    }
    return result;
  }
};

export default runPlan;
